/**
 * Rotational order and zone assignment for the 5-1 system.
 *
 * This module is the single source of truth for "who stands in which zone".
 * Everything else in the domain derives from `LINEUP` plus a rotation index —
 * there is no second table that could drift out of step with this one.
 *
 * FIVB 2025-2028 Rule 7.4.1 numbers the zones: 4/3/2 along the net from left to
 * right, and 5/6/1 behind them, again left to right. Rule 7.6.2 rotates players
 * clockwise through those zones: 2 -> 1, 1 -> 6, 6 -> 5, 5 -> 4, 4 -> 3, 3 -> 2.
 */

/**
 * Rotational order I..VI. In rotation 1 these players occupy zones 1..6 in this
 * order, because the next server is always the player standing in zone 2.
 */
export const LINEUP = ["S", "OH1", "MB1", "OPP", "OH2", "MB2"];

const START_ZONE = [1, 2, 3, 4, 5, 6];

const FRONT_ROW_ZONES = [2, 3, 4];

/**
 * The six zones in the order players travel them, which Rule 7.6.2 defines as
 * clockwise: 2 rotates to 1, 1 to 6, 6 to 5, and so on.
 *
 * Read as a map seen from above with the net at the top — 4 and 2 the far
 * corners, 5 and 1 the near ones — this sequence runs clockwise around the
 * court. That is a fact about the court, not about the interface, which is why
 * it lives here: the wheel in the HUD draws it, and it must not be mirrored for
 * a right-to-left language any more than the court itself is.
 */
export const CLOCKWISE_ZONES = [4, 3, 2, 1, 6, 5];

/** Roles that stand three zones apart and therefore never share a row. */
const PAIRS = [
  ["S", "OPP"],
  ["OH1", "OH2"],
  ["MB1", "MB2"],
];

function assertRotation(rotation: number) {
  if (!Number.isInteger(rotation) || rotation < 1 || rotation > 6) {
    throw new RangeError(`rotation must be an integer 1-6, got: ${rotation}`);
  }
}

/**
 * The zone a role occupies in a given rotation.
 *
 * Rotation `r` is named for the setter's zone as the setter walks
 * 1 -> 6 -> 5 -> 4 -> 3 -> 2, which is the convention every published 5-1 chart
 * uses.
 */
export function zoneOf(role: string, rotation: number) {
  const index = LINEUP.indexOf(role);
  if (index < 0) throw new Error(`unknown role: ${role}`);
  assertRotation(rotation);
  return ((START_ZONE[index] - rotation + 6) % 6) + 1;
}

/** Every role's zone for one rotation, as a Map preserving line-up order. */
export function slotsFor(rotation: number) {
  assertRotation(rotation);
  return new Map(LINEUP.map((role) => [role, zoneOf(role, rotation)]));
}

/** Zones 2, 3 and 4 are the front row (Rule 7.4.1.1). */
export const isFrontRow = (zone: number) => FRONT_ROW_ZONES.includes(zone);

/** The role standing three zones away — a role's opposite in the line-up. */
export function opposingRole(role: string) {
  for (const [a, b] of PAIRS) {
    if (a === role) return b;
    if (b === role) return a;
  }
  throw new Error(`unknown role: ${role}`);
}

/**
 * The rotation that puts a given role in a given zone — the inverse of
 * `zoneOf`, so the interface can offer "put me here" instead of only "next".
 */
export function rotationWhere(role: string, zone: number) {
  const index = LINEUP.indexOf(role);
  if (index < 0) throw new Error(`unknown role: ${role}`);
  if (!Number.isInteger(zone) || zone < 1 || zone > 6) {
    throw new RangeError(`no such zone: ${zone}`);
  }
  return ((START_ZONE[index] - zone + 6) % 6) + 1;
}

/** The rotations whose slot map satisfies `predicate`, in ascending order. */
export function rotationsWhere(predicate: (slots: Map<string, number>, rotation: number) => boolean) {
  const found = [];
  for (let rotation = 1; rotation <= 6; rotation += 1) {
    if (predicate(slotsFor(rotation), rotation)) found.push(rotation);
  }
  return found;
}
